import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import StudentDashboard from './pages/StudentDashboard';
import TrainerDashboard from './pages/TrainerDashboard';
import Layout from './components/Layout';
import UserManagement from './pages/UserManagement';
import CourseManagement from './pages/CourseManagement';
import BatchManagement from './pages/BatchManagement';
import ClassManagement from './pages/ClassManagement';
import AnalyticsPage from './pages/AnalyticsPage';
import AssignmentManagement from './pages/AssignmentManagement';
import StudentAssignments from './pages/StudentAssignments';
import Materials from './pages/Materials';
import TrainerMaterials from './pages/TrainerMaterials';
import Recordings from './pages/Recordings';
import TrainerRecordings from './pages/TrainerRecordings';
import AdminSelfPaced from './pages/AdminSelfPaced';
import StudentSelfPaced from './pages/StudentSelfPaced';
import TrainerFeed from './pages/TrainerFeed';
import StudentFeed from './pages/StudentFeed';
import StudentProgress from './pages/StudentProgress';
import TrainerProgress from './pages/TrainerProgress';
import StudentReport from './pages/StudentReport';
import CareerAIAdmin from './pages/CareerAIAdmin';
import CareerAIDashboard from './pages/CareerAIDashboard';
import CareerAIProfile from './pages/CareerAIProfile';
import StudyAssistant from './pages/StudyAssistant';
import StudentNotifications from './pages/StudentNotifications';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin w-10 h-10 text-primary" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const DashboardRedirect = () => {
  const { role } = useAuth();

  const dashboards = {
    student: <StudentDashboard />,
    trainer: <TrainerDashboard />,
    admin: <AdminDashboard />,
  };

  return dashboards[role] || <DashboardPlaceholder role="Unknown" />;
};

const PlaceholderPage = ({ title }) => (
  <Layout>
    <div className="flex items-center justify-center py-32 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-500">This feature is coming soon.</p>
      </div>
    </div>
  </Layout>
);

const placeholderRoutes = [
  { path: '/schedule', title: 'Schedule' },
];

const DashboardPlaceholder = ({ role }) => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{role} Dashboard</h1>
        <p className="text-gray-500 mb-1">Welcome, {user?.full_name || user?.email}!</p>
        <p className="text-sm text-gray-400 mb-6">Dashboard coming soon...</p>
        <button
          onClick={logout}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <CourseManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/batches"
            element={
              <ProtectedRoute>
                <BatchManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classes"
            element={
              <ProtectedRoute>
                <ClassManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer/assignments"
            element={
              <ProtectedRoute>
                <AssignmentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer/materials"
            element={
              <ProtectedRoute>
                <TrainerMaterials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/assignments"
            element={
              <ProtectedRoute>
                <StudentAssignments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/materials"
            element={
              <ProtectedRoute>
                <Materials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/recordings"
            element={
              <ProtectedRoute>
                <Recordings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer/recordings"
            element={
              <ProtectedRoute>
                <TrainerRecordings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/selfpaced"
            element={
              <ProtectedRoute>
                <AdminSelfPaced />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/selfpaced"
            element={
              <ProtectedRoute>
                <StudentSelfPaced />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <TrainerFeed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/feed"
            element={
              <ProtectedRoute>
                <StudentFeed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/progress"
            element={
              <ProtectedRoute>
                <StudentProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer/progress"
            element={
              <ProtectedRoute>
                <TrainerProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <StudentReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/careerai"
            element={
              <ProtectedRoute>
                <CareerAIAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/careerai"
            element={
              <ProtectedRoute>
                <CareerAIDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/careerai/profile"
            element={
              <ProtectedRoute>
                <CareerAIProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/careerai/study"
            element={
              <ProtectedRoute>
                <StudyAssistant />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/notifications"
            element={
              <ProtectedRoute>
                <StudentNotifications />
              </ProtectedRoute>
            }
          />
          {placeholderRoutes.map(({ path, title }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute>
                  <PlaceholderPage title={title} />
                </ProtectedRoute>
              }
            />
          ))}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
